// 4-tab bottom navigation для главных вкладок (D-S5-5 + Q-S5-S4-3).
// Каждая страница рендерит явно с правильным `active`.
// Source: docs/specs/sprint-5/stage-4-frontend.md §4.7.

import { useRef } from 'react';
import { Activity, Calendar, Home, User, type LucideIcon } from 'lucide-react';
import { Link } from 'wouter';
import { cn } from '@/lib/utils';
import { useBottomBarInset } from '@/hooks/useBottomBarInset';

export type BottomNavTab = 'home' | 'markers' | 'visits' | 'profile';

type Item = {
  key: BottomNavTab;
  href: string;
  label: string;
  icon: LucideIcon;
};

const ITEMS: Item[] = [
  { key: 'home', href: '/', label: 'Главная', icon: Home },
  { key: 'markers', href: '/markers', label: 'Показатели', icon: Activity },
  { key: 'visits', href: '/visits', label: 'Визиты', icon: Calendar },
  { key: 'profile', href: '/profile', label: 'Профиль', icon: User },
];

/**
 * `active` необязателен (HEAL-82): у подразделов своей вкладки нет.
 *
 * «Мои загрузки» — как раз такой случай: попадают туда и из Профиля, и со
 * строки на Главной, и меню нужно, чтобы экран не был тупиком. Подсветить
 * какую-то вкладку значило бы соврать — человек стоит не на ней.
 */
export function BottomNav({ active }: { active?: BottomNavTab }) {
  // Плашка уведомления встаёт НАД меню, а не поверх него: высоту сообщаем
  // сами, потому что у разных экранов панели разной высоты, а safe-area
  // меняется при повороте. См. useBottomBarInset.
  const navRef = useRef<HTMLElement>(null);
  useBottomBarInset(navRef);

  return (
    <nav
      ref={navRef}
      className="z-nav grid flex-shrink-0 grid-cols-4 border-t border-border bg-background"
    >
      {ITEMS.map((item) => {
        const Icon = item.icon;
        const isActive = item.key === active;
        return (
          <Link
            key={item.key}
            href={item.href}
            className={cn(
              'relative flex flex-col items-center justify-center gap-1 py-2 text-xs',
              isActive ? 'text-primary-text' : 'text-muted-foreground',
            )}
            aria-current={isActive ? 'page' : undefined}
          >
            {isActive && (
              <span className="absolute top-0 h-0.5 w-10 rounded-b bg-primary" />
            )}
            <Icon className="h-5 w-5" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
